import React, { useState, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, RoundedBox, Float, Line, ContactShadows } from '@react-three/drei';

const ConnectionLine = ({ start, end }) => {
    return (
        <Line
            points={[start, end]}
            color="#94a3b8"
            lineWidth={1}
            transparent
            opacity={0.2}
        />
    );
};

const BlockNode = ({ id, data, onClick }) => {
    const [hovered, setHover] = useState(false);
    const mesh = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        if (mesh.current) {
            mesh.current.rotation.x = Math.cos(t * 0.2 + data.position[0]) * 0.05;
            mesh.current.rotation.y = Math.sin(t * 0.2 + data.position[1]) * 0.05;
        }
    });

    // Dimensions optimized for fitting text
    const boxWidth = 3.4;
    const boxHeight = 1.3;

    return (
        <Float speed={2} rotationIntensity={0.1} floatIntensity={0.5}>
            <group position={data.position}>
                {/* The 3D Block */}
                <RoundedBox
                    ref={mesh}
                    args={[boxWidth, boxHeight, 0.4]}
                    radius={0.1}
                    smoothness={4}
                    onClick={(e) => { e.stopPropagation(); onClick(id); }}
                    onPointerOver={() => { document.body.style.cursor = 'pointer'; setHover(true); }}
                    onPointerOut={() => { document.body.style.cursor = 'auto'; setHover(false); }}
                >
                    <meshStandardMaterial
                        color={data.color}
                        roughness={0.3}
                        metalness={0.1}
                        envMapIntensity={1}
                    />
                </RoundedBox>

                {/* Shadow for depth */}
                <ContactShadows position={[0, -1, 0]} opacity={0.3} scale={6} blur={1.5} far={2} />

                {/* HTML Label */}
                <Html position={[0, 0, 0.26]} transform center pointerEvents="none" zIndexRange={[100, 0]}>
                    <div
                        className={`flex flex-col items-center justify-center text-center w-48 transition-all duration-300 ${hovered ? 'scale-105' : 'scale-100'}`}
                    >
                        <h3
                            className="text-2xl font-black text-white tracking-tighter leading-none drop-shadow-md m-0 w-full break-words select-none"
                            style={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}
                        >
                            {data.title}
                        </h3>
                        <p
                            className="text-[6px] font-bold text-white/80 uppercase tracking-widest mt-1 drop-shadow-sm m-0 w-full select-none px-1"
                            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
                        >
                            {data.subtitle}
                        </p>
                    </div>
                </Html>
            </group>
            <ConnectionLine start={[0, 0, 0]} end={[data.position[0] * -1, data.position[1] * -1, 0]} />
        </Float>
    );
};

const CenterNode = ({ onClick }) => {
    const [hovered, setHover] = useState(false);

    return (
        <group position={[0, 0, 0]}>
            <Float speed={4} rotationIntensity={0.2} floatIntensity={0.5}>
                <group
                    onClick={(e) => { e.stopPropagation(); onClick("ANF"); }}
                    onPointerOver={() => { document.body.style.cursor = 'pointer'; setHover(true); }}
                    onPointerOut={() => { document.body.style.cursor = 'auto'; setHover(false); }}
                >
                    <group>
                        <mesh>
                            <octahedronGeometry args={[1.1, 0]} />
                            <meshStandardMaterial color="#e2e8f0" roughness={0.1} metalness={0.1} transparent opacity={0.9} />
                        </mesh>
                        <mesh>
                            <octahedronGeometry args={[1.12, 0]} />
                            <meshBasicMaterial color="#3b82f6" wireframe transparent opacity={0.3} />
                        </mesh>
                    </group>

                    <Html position={[0, 0, 1.5]} center pointerEvents="none" zIndexRange={[100, 0]}>
                        <div className={`bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-white/50 transition-transform duration-300 ${hovered ? 'scale-110' : 'scale-100'}`}>
                            <h3 className="text-lg font-black text-gray-800 tracking-tighter m-0 select-none">
                                ANF CORE
                            </h3>
                        </div>
                    </Html>
                </group>
            </Float>
            <ContactShadows position={[0, -2.5, 0]} opacity={0.4} scale={10} blur={2} far={4} />
        </group>
    );
};

const MultiCoreVisual = ({ position }) => {
    // Render 3 small "ghost" ANF cores to represent multi-ANF orchestration
    const cores = [
        { pos: [1.5, 0.8, 0], color: "#60a5fa" },
        { pos: [2.2, 0, 0.5], color: "#3b82f6" },
        { pos: [1.5, -0.8, 0], color: "#2563eb" }
    ];

    return (
        <group position={position}>
            {cores.map((core, i) => (
                <group key={i} position={core.pos}>
                    <Float speed={3} rotationIntensity={0.5} floatIntensity={0.2} floatingRange={[-0.1, 0.1]}>
                        <mesh>
                            <octahedronGeometry args={[0.25, 0]} />
                            <meshStandardMaterial color={core.color} transparent opacity={0.8} />
                        </mesh>
                        <mesh>
                            <octahedronGeometry args={[0.28, 0]} />
                            <meshBasicMaterial color="white" wireframe transparent opacity={0.3} />
                        </mesh>
                    </Float>
                    {/* Connection line back to NCF block (local origin) */}
                    <Line
                        points={[[0, 0, 0], [-core.pos[0], -core.pos[1], -core.pos[2]]]}
                        color={core.color}
                        lineWidth={1}
                        transparent
                        opacity={0.3}
                    />
                </group>
            ))}
        </group>
    );
};

export const NRCFModel = ({ onSelect, selectedId, data }) => {
    return (
        <group>
            <BlockNode id="DA" data={data.DA} isSelected={selectedId === "DA"} onClick={onSelect} />
            <BlockNode id="AR" data={data.AR} isSelected={selectedId === "AR"} onClick={onSelect} />
            <BlockNode id="EV" data={data.EV} isSelected={selectedId === "EV"} onClick={onSelect} />

            {/* NCF Block with Multi-Core Visual */}
            <group>
                <BlockNode id="NCF" data={data.NCF} isSelected={selectedId === "NCF"} onClick={onSelect} />
                <MultiCoreVisual position={data.NCF.position} />
            </group>

            <CenterNode isSelected={selectedId === "ANF"} onClick={onSelect} />
        </group>
    );
};
